/*
  Advertorial lander (layout/xall-adv.liquid). Four jobs, no dependencies:
  1. Attribution: capture ad-click parameters into sessionStorage (key below) so the later
     offer page can stamp them as cart attributes, and carry them onto same-origin CTA links
     ([data-xadv-cta]) so they survive a new tab. Stored shape:
     { utm_source: '...', gclid: '...', ..., landing_path: '/pages/...', adv_cta: 'img-image_003' }
     Paragraph links can't carry attributes from the editor, so any .xadv-article link whose
     path is an offer page (the section's data-xadv-offer-url, or the path of an existing
     [data-xadv-cta] link) becomes a CTA too: data-xadv-cta="inline-<n>", in page order.
  2. CTA readout: a click on any [data-xadv-cta] link stores its id as adv_cta (last click wins);
     the offer page writes it to the cart as the adv_cta attribute.
  3. Sticky CTA bar: shown once the reader reaches the block flagged "Start the sticky bar
     here" ([data-xadv-sticky-anchor], its top edge on or above the viewport bottom), or, when no
     block is flagged, once the hero has scrolled away. Hidden while an in-article CTA button is
     on screen (.xadv-cta__btn only; image and inline links never hide it). Height is reserved in
     CSS, so toggling never shifts layout.
  4. Events, so the article shows where readers leave relative to the buy buttons. Each one goes
     three ways with the same payload, exactly like assets/xall-adv-offer.js:
     - window.dataLayer.push({ event: 'adv_scroll', ... })
     - document CustomEvent 'xall:adv_scroll' (detail = payload)
     - Shopify.analytics.publish('xall_adv_scroll', payload) for web pixels, when present. The
       store's Vendo pixel subscribes to all_custom_events and sends each one to Mixpanel under
       its own name (xall_adv_scroll, ...), with this payload laid over its own properties.
       Verified on a development theme 2026-10-16.
     Names:
     - adv_scroll: depth 25, 50, 75, 100, once each per page view. Measured against the article
       content only: from the top of the first .xadv-article to the top of the footer block
       (.xadv-footer), or to the bottom of the last .xadv-article when there is no footer, so the
       footer never inflates it. 100 = the end of the content reached the viewport bottom. A jump
       past several milestones sends each one passed, in order.
     - adv_cta_seen: cta = the link's data-xadv-cta, once per CTA element per page view, when an
       in-article CTA (.xadv-article a[data-xadv-cta], which includes the .xadv-cta__btn buttons)
       is at least half visible, or fills at least half the viewport when it is taller than that.
       Linked images (img-<block id>) are not counted as seen: scroll depth already shows how far
       readers get past them, and one event per image is noise. The sticky bar is not an
       in-article CTA, so it never counts as seen either.
     - adv_cta_click: cta = the clicked link's id (images and sticky included), sent from the CTA
       readout click handler right before navigation, so a click can be lost; the offer page's
       adv_offer_view (adv_cta, landing_path) is the reliable arrival signal.
     Payload: depth_pct (furthest article depth reached so far, integer 0 to 100), article_path
     (location.pathname), utm_source, utm_medium, utm_campaign, utm_content, utm_term from the saved
     attribution (only when set, so a blank never overwrites Vendo's own URL values), plus
     depth or cta. The article section exposes no angle or offer_id, so none are sent.
     Not on the offer page: it shares this layout and script, so tracking stays off when the page
     has a stand-alone [data-xadv-offer] or no article content above the footer. A buy box placed
     inside the article ([data-xadv-offer-embedded], see 5.) keeps tracking on; depth then ends at
     the top of that buy box (100 = reached it), and its checkout button (data-xadv-cta="picker")
     counts for adv_cta_seen.
     Back/forward cache: a restored page is the same page view, so milestones and CTAs already sent
     are not sent again (clicks always are). A reload or a new visit starts over.
  5. Buy box on this page ([data-xadv-offer-embedded], sections/xall-adv-offer.liquid placement
     "Inside the article"): every offer link on the page (sticky bar, CTA link and button blocks,
     linked images, tagged inline links) glides to the buy box instead of leaving for the offer
     page, landing with its checkout button in view (see landingBox() and glide() below). The links keep their offer-page href in the markup as the no-JavaScript fallback; the
     script swaps it for #<buy box id>, so there is nothing to set in the editor. The click is
     still recorded (adv_cta, adv_cta_click) before the jump. The sticky bar also hides while any
     part of the buy box is on screen.
  6. Sticky target (no buy box on the page): when an article heading is flagged "Sticky bar scrolls
     here" ([data-xadv-sticky-target], sections/xall-advertorial.liquid), only the sticky bar's
     button glides to it, landing like 5. with the first CTA button after that heading in view
     (the heading on top when the span fits). Every other link still goes to the offer page. The
     click is recorded as adv_cta "sticky" before the glide; the CTA button tapped next then
     overwrites it (last click wins). Without JavaScript the bar keeps its offer-page href.
  7. Relative byline date ([data-xadv-date-days-ago], byline setting "Date: days before today"):
     Liquid prints today minus N days at render time; the script rewrites it from the visitor's
     own clock, so a cached page never shows a stale date.
  8. Sticky bar delay test (sticky section setting "A/B test: delay the bar", rendered as
     data-xadv-sticky-test="<test id>" on the bar; off by default, so pages without it are
     unchanged). Each visitor gets arm a or b, 50/50, kept in localStorage
     ('xall_sticky_test' = { id, arm }) so the same visitor sees the same arm on later visits; a
     new test id re-draws. Storage blocked: a fresh draw per page view. ?sticky_arm=a|b forces an
     arm for QA (not stored). Arm a: the bar behaves as described in 3. Arm b: the bar waits until
     the reader reaches the block flagged "Start the delayed sticky bar here"
     ([data-xadv-sticky-anchor-delayed]; without one, the first block after the hero), same edge
     rule as the flagged anchor; hiding over CTA buttons and the buy box is unchanged. The theme editor always shows arm a and draws nothing.
     The arm goes everywhere it is read: sticky_arm on every event payload (4.), the saved
     attribution (so assets/xall-adv-offer.js writes it as the sticky_arm cart attribute and on its
     own events), and a Microsoft Clarity custom tag (clarity('set', 'sticky_arm', arm)).
     Two extra events, both arms, once per page view: adv_sticky_arm (arm, test; sent on load, the
     per-arm visit count) and adv_past_hero (arm, test; the first block after the hero reached
     the viewport bottom, i.e. the reader got past the first screen). 1 - adv_past_hero /
     adv_sticky_arm is the per-arm first-screen bounce.
*/
(function () {
  'use strict';

  var STORAGE_KEY = 'xall_adv_attribution';
  var KEYS = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id',
    'gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid', 'msclkid',
    'affid', 'sub1', 'sub2', 'sub3', 'sub4', 'sub5'
  ];
  var EVENT_UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  var MILESTONES = [25, 50, 75, 100];
  var maxDepth = 0;

  function read() {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}') || {};
    } catch (e) {
      return {};
    }
  }

  function capture() {
    var saved = read();
    var params = new URLSearchParams(window.location.search);
    KEYS.forEach(function (key) {
      var value = params.get(key);
      if (value) saved[key] = value.slice(0, 500);
    });
    if (!saved.landing_path) saved.landing_path = window.location.pathname;
    write(saved);
    return saved;
  }

  function write(saved) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(saved));
    } catch (e) {}
  }

  function sameOrigin(raw) {
    if (!raw || raw.charAt(0) === '#') return null;
    var url;
    try {
      url = new URL(raw, window.location.href);
    } catch (e) {
      return null;
    }
    return url.origin === window.location.origin ? url : null;
  }

  // Tag paragraph links that point at the offer page as inline CTAs.
  function tagInline() {
    var paths = {};
    var sources = document.querySelectorAll('[data-xadv-offer-url], a[data-xadv-cta]');
    Array.prototype.forEach.call(sources, function (node) {
      var url = sameOrigin(node.getAttribute('data-xadv-offer-url') || node.getAttribute('href'));
      if (url) paths[url.pathname.replace(/\/+$/, '')] = true;
    });
    var n = 0;
    var links = document.querySelectorAll('.xadv-article a[href]:not([data-xadv-cta])');
    Array.prototype.forEach.call(links, function (link) {
      var url = sameOrigin(link.getAttribute('href'));
      if (!url || !paths[url.pathname.replace(/\/+$/, '')]) return;
      n += 1;
      link.setAttribute('data-xadv-cta', 'inline-' + n);
    });
  }

  // Remember which CTA the reader used. Captured before navigation; last click wins.
  function readout() {
    document.addEventListener(
      'click',
      function (event) {
        var link = event.target && event.target.closest && event.target.closest('a[data-xadv-cta]');
        if (!link) return;
        var saved = read();
        saved.adv_cta = String(link.getAttribute('data-xadv-cta') || '').slice(0, 100);
        write(saved);
        if (tracking) {
          measure();
          emit('adv_cta_click', { cta: saved.adv_cta });
        }
      },
      true
    );
  }

  function carry(saved) {
    var links = document.querySelectorAll('a[data-xadv-cta]');
    Array.prototype.forEach.call(links, function (link) {
      var url = sameOrigin(link.getAttribute('href'));
      if (!url) return;
      var changed = false;
      KEYS.forEach(function (key) {
        if (saved[key] && !url.searchParams.has(key)) {
          url.searchParams.set(key, saved[key]);
          changed = true;
        }
      });
      if (changed) link.setAttribute('href', url.pathname + url.search + url.hash);
    });
  }

  var picker = document.querySelector('[data-xadv-offer-embedded]');

  // 5. Where a CTA lands: the checkout button fully on screen, at least GAP px above the bottom of
  // the visible viewport (visualViewport on phones), with as much of the box above it as fits.
  // If the heading through the button fits, that span is centered instead, but never so low that
  // the page shows anything above the box's top (minus its scroll-margin-top). The article sticky
  // bar is not reserved: it hides whenever the box is on screen, and this layout has no header.
  var GAP = 20;

  function landingBox() {
    var vh = window.visualViewport ? window.visualViewport.height : window.innerHeight;
    var y = window.pageYOffset;
    var box = picker.getBoundingClientRect();
    var main = picker.querySelector('[data-xadv-offer-main]') || picker.querySelector('[data-xadv-cta="picker"]');
    var heading = picker.querySelector('[data-xadv-offer-heading]');
    var margin = parseFloat(window.getComputedStyle(picker).scrollMarginTop) || 0;
    var ceiling = y + box.top - margin;
    var target = ceiling;
    if (main) {
      var top = y + (heading ? heading.getBoundingClientRect().top : box.top);
      var bottom = y + main.getBoundingClientRect().bottom;
      var anchor = bottom + GAP - vh;
      target = bottom - top + GAP * 2 <= vh ? Math.min((top + bottom - vh) / 2, ceiling) : anchor;
      target = Math.max(target, anchor);
    }
    var max = document.documentElement.scrollHeight - window.innerHeight;
    return Math.max(0, Math.min(target, max));
  }

  // Eased glide (ease-in-out, 700 ms plus 1 ms per 20 px, at most 1100 ms). The landing is
  // re-measured every frame, so images loading on the way cannot throw it off. Wheel or touch
  // stops it; reduced motion jumps straight there.
  var stopGlide = null;

  function glide(to) {
    var landing = to || landingBox;
    if (stopGlide) stopGlide();
    var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var from = window.pageYOffset;
    var distance = Math.abs(landing() - from);
    if (still || distance < 2 || !window.requestAnimationFrame) {
      window.scrollTo(0, landing());
      return;
    }
    var duration = Math.min(1100, 700 + distance / 20);
    var start = null;
    var frame = 0;
    function stop() {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('wheel', stop);
      window.removeEventListener('touchstart', stop);
      stopGlide = null;
    }
    function step(now) {
      if (start === null) start = now;
      var t = Math.min(1, (now - start) / duration);
      var eased = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      window.scrollTo(0, from + (landing() - from) * eased);
      if (t < 1) frame = window.requestAnimationFrame(step);
      else stop();
    }
    window.addEventListener('wheel', stop, { passive: true });
    window.addEventListener('touchstart', stop, { passive: true });
    stopGlide = stop;
    frame = window.requestAnimationFrame(step);
  }

  // 6. Where the sticky bar lands on a page with a sticky target: the same rule as landingBox(),
  // with the flagged heading as the top and the first in-article CTA button after it as the
  // button to keep in view.
  var stickyTarget = picker ? null : document.querySelector('.xadv-article [data-xadv-sticky-target]');

  function targetButton() {
    var buttons = document.querySelectorAll('.xadv-article .xadv-cta__btn');
    for (var i = 0; i < buttons.length; i += 1) {
      if (stickyTarget.compareDocumentPosition(buttons[i]) & Node.DOCUMENT_POSITION_FOLLOWING) return buttons[i];
    }
    return null;
  }

  function landingTarget() {
    var vh = window.visualViewport ? window.visualViewport.height : window.innerHeight;
    var y = window.pageYOffset;
    var margin = parseFloat(window.getComputedStyle(stickyTarget).scrollMarginTop) || 0;
    var ceiling = y + stickyTarget.getBoundingClientRect().top - margin;
    var target = ceiling;
    var main = targetButton();
    if (main) {
      var top = ceiling;
      var bottom = y + main.getBoundingClientRect().bottom;
      var anchor = bottom + GAP - vh;
      target = bottom - top + GAP <= vh ? ceiling : anchor;
      target = Math.max(target, anchor);
    }
    var max = document.documentElement.scrollHeight - window.innerHeight;
    return Math.max(0, Math.min(target, max));
  }

  function stickyToTarget() {
    var link = document.querySelector('[data-xadv-sticky-link]');
    if (!stickyTarget || !stickyTarget.id || !link) return;
    link.setAttribute('href', '#' + stickyTarget.id);
    link.addEventListener('click', function (event) {
      if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
      event.preventDefault();
      glide(landingTarget);
    });
  }

  // 7. Byline date as today minus N days, from the visitor's clock.
  function relativeDates() {
    document.querySelectorAll('[data-xadv-date-days-ago]').forEach(function (el) {
      var days = parseInt(el.getAttribute('data-xadv-date-days-ago'), 10);
      if (isNaN(days)) return;
      var date = new Date();
      date.setDate(date.getDate() - days);
      el.textContent = date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    });
  }

  // Point every offer link at the buy box on this page; glide there on click.
  function repoint() {
    if (!picker || !picker.id) return;
    var links = document.querySelectorAll('a[data-xadv-cta]');
    Array.prototype.forEach.call(links, function (link) {
      link.setAttribute('href', '#' + picker.id);
    });
    document.addEventListener('click', function (event) {
      var link = event.target && event.target.closest && event.target.closest('a[data-xadv-cta]');
      if (!link || event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
      event.preventDefault();
      glide();
      var heading = picker.querySelector('[data-xadv-offer-heading]');
      if (heading) {
        try {
          heading.focus({ preventScroll: true });
        } catch (e) {}
      }
    });
  }

  function sticky() {
    var bar = document.querySelector('[data-xadv-sticky]');
    if (!bar) return;
    var link = bar.querySelector('[data-xadv-sticky-link]');
    var delayed = stickyTest && stickyTest.arm === 'b'
      ? document.querySelector('.xadv-article [data-xadv-sticky-anchor-delayed]') || pastHero
      : null;
    var anchor = delayed || document.querySelector('.xadv-article [data-xadv-sticky-anchor]');
    var trigger =
      anchor ||
      document.querySelector('[data-xadv-hero]') ||
      document.querySelector('.xadv-article .xadv-figure') ||
      document.querySelector('.xadv-article h1');

    function set(on) {
      bar.classList.toggle('is-on', on);
      bar.setAttribute('aria-hidden', on ? 'false' : 'true');
      if (link) link.tabIndex = on ? 0 : -1;
    }

    if (!trigger || !('IntersectionObserver' in window)) {
      set(true);
      return;
    }

    var reached = false;
    var ctasInView = new Set();
    var ctas = document.querySelectorAll('.xadv-article .xadv-cta__btn');

    function update() {
      set(reached && ctasInView.size === 0);
    }

    set(false);
    if (anchor) {
      // The root is stretched far above the viewport, so "intersecting" means the anchor's top
      // edge is at or above the viewport bottom. That stays correct when the reader jumps past
      // the anchor (back to top, in-page link) without it ever crossing the visible area.
      new IntersectionObserver(
        function (entries) {
          reached = entries[entries.length - 1].isIntersecting;
          update();
        },
        { rootMargin: '100000px 0px 0px 0px' }
      ).observe(anchor);
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.target === trigger) {
          reached = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        } else if (entry.isIntersecting) {
          ctasInView.add(entry.target);
        } else {
          ctasInView.delete(entry.target);
        }
      });
      update();
    });
    if (!anchor) io.observe(trigger);
    Array.prototype.forEach.call(ctas, function (cta) {
      io.observe(cta);
    });
    if (picker) io.observe(picker);
  }

  /* ---------- sticky bar delay test (see 8. above) ---------- */

  var TEST_KEY = 'xall_sticky_test';

  function drawStickyTest() {
    var bar = document.querySelector('[data-xadv-sticky][data-xadv-sticky-test]');
    if (!bar || (window.Shopify && window.Shopify.designMode)) return null;
    var id = String(bar.getAttribute('data-xadv-sticky-test') || '').slice(0, 50);
    if (!id) return null;
    var forced = new URLSearchParams(window.location.search).get('sticky_arm');
    if (forced === 'a' || forced === 'b') return { id: id, arm: forced };
    var stored = null;
    try {
      stored = JSON.parse(localStorage.getItem(TEST_KEY) || 'null');
    } catch (e) {}
    if (stored && stored.id === id && (stored.arm === 'a' || stored.arm === 'b')) return stored;
    var test = { id: id, arm: Math.random() < 0.5 ? 'a' : 'b' };
    try {
      localStorage.setItem(TEST_KEY, JSON.stringify(test));
    } catch (e) {}
    return test;
  }

  // The first block after the hero: where adv_past_hero fires (and arm b's start when no block is
  // flagged "Start the delayed sticky bar here").
  function findPastHero() {
    var list = document.querySelectorAll('.xadv-article');
    return list.length > 1 ? list[1].querySelector('.xadv-block') : null;
  }

  function tagStickyTest(saved) {
    saved.sticky_arm = stickyTest.arm;
    write(saved);
    try {
      if (typeof window.clarity === 'function') window.clarity('set', 'sticky_arm', stickyTest.arm);
    } catch (e) {}
  }

  function pastHeroEvent() {
    if (!pastHero || !('IntersectionObserver' in window)) return;
    // Same edge rule as the anchor in sticky(): its top edge on or above the viewport bottom.
    var io = new IntersectionObserver(
      function (entries) {
        if (!entries[entries.length - 1].isIntersecting) return;
        io.disconnect();
        measure();
        emit('adv_past_hero', { arm: stickyTest.arm, test: stickyTest.id });
      },
      { rootMargin: '100000px 0px 0px 0px' }
    );
    io.observe(pastHero);
  }

  /* ---------- events (see 4. above) ---------- */

  function payload(extra) {
    var saved = read();
    var base = {
      depth_pct: maxDepth,
      article_path: window.location.pathname
    };
    EVENT_UTM_KEYS.forEach(function (key) {
      if (saved[key]) base[key] = String(saved[key]).slice(0, 500);
    });
    if (stickyTest) base.sticky_arm = stickyTest.arm;
    return Object.assign(base, extra || {});
  }

  function emit(name, extra) {
    var detail = payload(extra);
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, detail));
    } catch (e) {}
    try {
      document.dispatchEvent(new CustomEvent('xall:' + name, { detail: detail }));
    } catch (e) {}
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('xall_' + name, detail);
      }
    } catch (e) {}
  }

  var articles = document.querySelectorAll('.xadv-article');
  var standaloneOffer = document.querySelector('[data-xadv-offer]:not([data-xadv-offer-embedded])');
  var tracking = articles.length > 0 && !standaloneOffer;

  // Article content bounds in viewport coordinates, re-read every time (lazy images move them).
  function bounds() {
    var top = articles[0].getBoundingClientRect().top;
    var footers = articles[articles.length - 1].querySelectorAll('.xadv-footer');
    var end = picker
      ? picker.getBoundingClientRect().top
      : footers.length
      ? footers[0].getBoundingClientRect().top
      : articles[articles.length - 1].getBoundingClientRect().bottom;
    return { top: top, end: end };
  }

  // Updates maxDepth: how far the viewport bottom has reached into the article content.
  function measure() {
    var b = bounds();
    var span = b.end - b.top;
    if (span <= 0) return;
    var pct = Math.floor(((window.innerHeight - b.top) / span) * 100);
    pct = Math.max(0, Math.min(100, pct));
    if (pct > maxDepth) maxDepth = pct;
  }

  function scrollDepth() {
    var sent = 0;
    var queued = false;

    function check() {
      queued = false;
      measure();
      while (sent < MILESTONES.length && maxDepth >= MILESTONES[sent]) {
        emit('adv_scroll', { depth: MILESTONES[sent] });
        sent += 1;
      }
      if (sent >= MILESTONES.length) {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
      }
    }

    function onScroll() {
      if (queued) return;
      queued = true;
      if (window.requestAnimationFrame) window.requestAnimationFrame(check);
      else setTimeout(check, 100);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    check();
  }

  function ctaSeen() {
    if (!('IntersectionObserver' in window)) return;
    // Linked images (img-<block id>) are left out; see 4. above.
    var links = Array.prototype.filter.call(
      document.querySelectorAll('.xadv-article a[data-xadv-cta], [data-xadv-offer-embedded] [data-xadv-cta]'),
      function (link) {
        return String(link.getAttribute('data-xadv-cta') || '').indexOf('img-') !== 0;
      }
    );
    if (!links.length) return;
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var viewport = (entry.rootBounds && entry.rootBounds.height) || window.innerHeight;
          if (entry.intersectionRatio < 0.5 && entry.intersectionRect.height < viewport / 2) return;
          io.unobserve(entry.target);
          measure();
          var cta = String(entry.target.getAttribute('data-xadv-cta') || '').slice(0, 100);
          emit('adv_cta_seen', { cta: cta });
        });
      },
      { threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1] }
    );
    links.forEach(function (link) {
      io.observe(link);
    });
  }

  var stickyTest = drawStickyTest();
  var pastHero = stickyTest ? findPastHero() : null;
  relativeDates();
  tagInline();
  var saved = capture();
  if (stickyTest) tagStickyTest(saved);
  if (picker) repoint();
  else carry(saved);
  if (stickyTarget) stickyToTarget();
  readout();
  sticky();
  if (tracking) {
    if (stickyTest) {
      emit('adv_sticky_arm', { arm: stickyTest.arm, test: stickyTest.id });
      pastHeroEvent();
    }
    scrollDepth();
    ctaSeen();
  }
})();
